import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Button from './Button';
import Select from './Select';
import BasicInput from './BasicInput';
import ConfirmDialog from './ConfirmDialog';
import RarityBadge from '@/components/RarityBadge';
import { getSaleShipmentPreparation } from '@/api/sales';
import backIcon from '@/assets/chevron_left.svg';
import closeIcon from '@/assets/close.svg';
import warningIcon from '@/assets/warning_orange.svg';
import { getProductThumbnailUrl } from '@/utils/imageUtils';

const ShippingInputPanel = ({ isOpen, onClose, onSubmit, sale }) => {
  const { t } = useTranslation();
  const [courierCompany, setCourierCompany] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const panelRef = useRef(null);
  
  const { data: preparationData, isLoading } = useQuery({
    queryKey: ['shipmentPreparation', sale?.id],
    queryFn: () => getSaleShipmentPreparation(sale?.id),
    enabled: !!sale?.id && isOpen,
  });

  // Reset states when panel is closed
  useEffect(() => {
    if (!isOpen) {
      setCourierCompany('');
      setTrackingNumber('');
      setShowConfirmDialog(false);
    }
  }, [isOpen]);

  const handleCourierChange = (value) => {
    setCourierCompany(value);
  };

  const handleTrackingChange = (e) => {
    const value = e.target.value;
    // Only allow digits and maximum 12 characters
    if (/^\d*$/.test(value) && value.length <= 12) {
      setTrackingNumber(value);
    }
  };

  const handleSubmit = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = () => {
    setShowConfirmDialog(false);
    onSubmit({
      courier: courierCompany,
      trackingNumber: trackingNumber
    });
  };

  const handleCancelConfirm = () => {
    setShowConfirmDialog(false);
  };

  const isFormValid = courierCompany && trackingNumber;

  // Courier options from API or fallback
  const courierOptions = preparationData?.data?.availableCouriers 
    ? preparationData.data.availableCouriers.map(courier => ({
        value: courier.code,
        label: courier.name
      }))
    : [];

  // Get shipping address from API or use fallback
  const shippingAddress = preparationData?.data?.shippingAddress || '';

  // Format address with line breaks
  const formatAddress = (address) => {
    if (!address) return t('shipping.placeholderAddress');
    
    // Already has line breaks
    if (address.includes('\n')) return address;
    
    // Try to add line breaks at meaningful points
    return address.replace(/,/g, ',\n').replace(/\s+(?=\+)/g, '\n');
  };

  // Don't render at all if the panel should not be visible
  if (!isOpen) return null;

  return (
    <div 
      ref={panelRef}
      className={`fixed top-0 right-0 w-[480px] h-full bg-white flex flex-col items-start z-50}`}
    >
      {/* Header */}
      <div className="w-full h-[72px] py-6 px-6 flex justify-between items-center">
        <div className="inline-flex justify-start items-center gap-2">
          <button 
            onClick={onClose}
            className="w-4 h-4"
          >
              <img src={backIcon} alt='back' className='w-4 h-4'/>
          </button>
          <div className="font-bold">{t('shipping.inputTitle')}</div>
        </div>
        <button 
          onClick={onClose}
          className="w-6 h-6 flex items-center justify-center"
        >
          <img src={closeIcon} alt="close" className="w-7 h-7" />
        </button>
      </div>

      {/* Form Content */}
      <div className="self-stretch px-6 pb-[120px] bg-white rounded-md flex flex-col justify-start items-start overflow-y-auto">
        {isLoading ? (
          <div className="w-full h-full flex justify-center items-center">
            <p>{t('common.loading')}</p>
          </div>
        ) : (
          <div className="self-stretch flex flex-col justify-start items-start gap-4">
            {/* Warning Bar */}
            <div className="w-full p-3 flex items-center gap-2 bg-[#ffecc9] rounded-md outline outline-1 outline-offset-[-1px] outline-[#ffecc9] text-sm text-[#af7300]">
              <img src={warningIcon} alt='warning' className='w-4 h-4' />
              {t('shipping.warningMessage')}
            </div>
            
            {/* Sale Item Info */}
            <div className="self-stretch bg-white rounded-md flex flex-col justify-start items-start gap-1.5">
              <div className="text-[#666666] text-sm">{t('shipping.productLabel')}</div>
              <div className="self-stretch p-3 rounded-md outline outline-1 outline-offset-[-1px] outline-[#dddddd] inline-flex justify-start items-center gap-3">
                <div className="w-[84px] h-[84px] relative bg-white border border-[#DDDDDD] rounded">
                  <img
                    className="w-full h-full object-contain"
                    src={getProductThumbnailUrl(sale.listing.product.images?.[0]?.url)}
                    alt={sale.listing.product.localizedName || ""}
                  />
                </div>
                <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                  {/* Title with rarity */}
                  <div className="self-stretch text-[#14151a] text-sm font-medium">
                    {sale?.listing?.product?.localizedRawName || sale?.listing?.product?.localizedName || t('shipping.placeholderProductName')}
                    {sale?.listing?.product?.rarityInfo?.sub?.name && ` (${sale.listing.product.rarityInfo.sub.name})`}
                  </div>

                  {/* Subtitle with set info, code, and notes */}
                  <div className="self-stretch flex flex-col justify-start items-start gap-0.5">
                    <div className="self-stretch inline-flex justify-start items-center gap-1">
                      <span className="flex-1 justify-start text-xs leading-[18px]">
                        {/* Rarity badge */}
                        {sale?.listing?.product?.rarityInfo?.main && (
                          <RarityBadge
                            rarity={sale.listing.product.rarityInfo.main}
                            className="mr-1"
                            textClassName="text-xs leading-[12px]"
                          />
                        )}
                        {/* Code and notes */}
                        {sale?.listing?.product?.set?.code && (
                          <span className="h-[18px] px-1 bg-[#f4f4f4] rounded-[3px] outline outline-[0.50px] outline-offset-[-0.50px] outline-[#dddddd] inline-flex justify-start items-center gap-2.5">
                            <span className="justify-start text-[#111111] text-sm font-normal leading-[14px]">
                              {sale.listing.product.set.code}{sale?.listing?.product?.rawCode ? ` ${sale.listing.product.rawCode}` : ''}
                            </span>
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="self-stretch flex flex-col justify-start items-start gap-1">
              <div className="self-stretch inline-flex justify-start items-start gap-3">
                <div className="flex-1 flex justify-start items-center gap-1">
                  <div className="text-[#111111] text-sm leading-[21px]">{t('shipping.shippingAddressLabel')}</div>
                </div>
              </div>
              <div className="self-stretch min-h-[105px] text-black text-sm leading-[21px] whitespace-pre-line">
                {formatAddress(shippingAddress)}
              </div>
            </div>

            {/* Shipping Company Dropdown */}
            <div className="self-stretch h-[74px] relative">
              <div className="left-0 top-0 absolute text-[#111111] text-sm leading-[21px]">{t('shipping.courierLabel')}</div>
              <div className="w-full mt-[25.34px]">
                <Select 
                  options={courierOptions}
                  value={courierCompany}
                  onValueChange={handleCourierChange}
                  placeholder={t('shipping.selectCourier')}
                  className="w-full h-12 justify-between text-start rounded-md bg-[#f7f7f7]"
                  contentClassName="z-50"
                />
              </div>
            </div>

            {/* Tracking Number Input */}
            <div className="self-stretch">
              <BasicInput
                title={t('shipping.trackingNumberLabel')}
                value={trackingNumber}
                onChange={handleTrackingChange}
                placeholder={t('shipping.trackingNumberPlaceholder')}
                containerClassName="w-full"
              />
            </div>
          </div>
        )}
      </div>

      {/* Fixed Bottom Button */}
      <div className="w-full px-6 py-4 bg-white fixed bottom-0 right-0" style={{ width: '480px' }}>
        <Button
          onClick={handleSubmit}
          variant="primary"
          size="xl"
          disabled={!isFormValid || isLoading}
          className="w-full"
        >
          {t('shipping.submitButton')}
        </Button>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={handleCancelConfirm}
        onConfirm={handleConfirmSubmit}
        title={t('shipping.confirmTitle')}
        description={t('shipping.warningMessage')}
        cancelText={t('common.cancel', 'Cancel')}
        confirmText={t('shipping.submitButton')}
      />
    </div>
  );
};

export default ShippingInputPanel; 
